import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Eye, Globe, ArrowRight, Music, BookMarked, GraduationCap, Users2, Sparkles, Check, Palette, Utensils, HeartPulse, Shirt, Library } from "lucide-react";
import { IMAGES } from "../lib/images";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";

export default function About() {
  useSEO(
    "About Us",
    "ÀsÀNigHT Worldwide is a cultural platform preserving and sharing Yorùbá heritage, philosophy, and history with a global audience. Based in Pawtucket, Rhode Island."
  );

  const [banner, setBanner] = React.useState(IMAGES.manScarification);
  React.useEffect(() => {
    api.get("/site-settings").then((r) => { if (r.data?.banner_about) setBanner(r.data.banner_about); }).catch(() => {});
  }, []);

  return (
    <div>
      <header className="relative min-h-[56vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner} alt="Yorùbá heritage" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Our Story</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero"
          >
            About ÀsÀNigHT Worldwide
          </motion.h1>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-20">
        <Reveal>
          <p className="text-2xl md:text-3xl font-serif text-forest leading-relaxed border-l-2 border-gold pl-6 mb-10">
            ÀsÀNigHT Worldwide shares the richness of Yorùbá heritage through educational
            content and historical insights — making a profound culture accessible to learners
            everywhere.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="prose-yoruba text-lg text-ink-muted space-y-5">
            <p>
              ÀsÀNigHT Worldwide is a non-profit and non-governmental organization that supports
              the activities of volunteers — cultural entrepreneurs, artists, teachers, dancers,
              theatre practitioners, and creative groups. We are dedicated to providing Yorùbá
              language learning and cultural programmes of the highest quality to our diverse
              community of children, teens, and adults alike.
            </p>
            <p>
              We have created a dynamic cultural organization to be established in major cities in
              Nigeria and worldwide. ÀsÀNigHT Worldwide preserves and promotes Yorùbá cultural
              heritage, educates the public about our local history, and organizes Yorùbá cultural
              life across the globe.
            </p>
            <p>
              We hunt for talent in all facets of creative expression. We honour and respect the
              creativity and diversity of Yorùbá tradition and cultural heritage. We also raise,
              invest, and distribute funds for the benefit of Yorùbá language learning, culture,
              customs, and community.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-sand border-y border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8">
          {[
            { icon: Target, title: "Our Mission", text: "To educate, preserve, and inspire — sharing authentic Yorùbá knowledge and digitally archiving traditions for future generations." },
            { icon: Eye, title: "Our Vision", text: "Sustaining the Yorùbá cultural heritage worldwide — for this generation and every one that follows." },
            { icon: Globe, title: "Global Education", text: "Connecting the diaspora and the curious worldwide with the depth of Yorùbá philosophy, art, and spirituality." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="bg-cream border border-parchment rounded-xl p-8 h-full">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream mb-6">
                  <c.icon className="w-5 h-5" />
                </span>
                <h3 className="font-serif text-2xl text-ink mb-3">{c.title}</h3>
                <p className="text-ink-muted leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="adire-bg grain-overlay">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-5">Our Mission Statement</p>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-cream leading-snug">
              ÀsÀNigHT Worldwide is a non-profit organization dedicated to supporting and
              promoting Yorùbá cultural heritage through{" "}
              <span className="text-gold">education, arts, music, food, health &amp; wellness, fashion, and library.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-cream/70 text-sm uppercase tracking-[0.2em]">
              Our Vision
            </p>
            <p className="font-serif italic text-2xl md:text-3xl text-gold-soft mt-2">
              Sustaining the Yorùbá Cultural Heritage Worldwide.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {[
                { icon: GraduationCap, label: "Education" },
                { icon: Palette, label: "Arts" },
                { icon: Music, label: "Music" },
                { icon: Utensils, label: "Food" },
                { icon: HeartPulse, label: "Health & Wellness" },
                { icon: Shirt, label: "Fashion" },
                { icon: Library, label: "Library" },
              ].map((p) => (
                <span
                  key={p.label}
                  data-testid={`mission-pillar-${p.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="inline-flex items-center gap-2 bg-cream/5 border border-cream/20 text-cream/90 rounded-full px-4 py-2 text-sm"
                >
                  <p.icon className="w-4 h-4 text-gold" />
                  {p.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">What We Do</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink max-w-2xl mb-14">
            Culture, in every facet of life
          </h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Music, title: "Cultural Events", text: "We organize music, concerts, and performances all year round, promoting Yorùbá musicians, artists, dance, and theatre — and we attend annual festivals in Yorùbá towns and villages." },
            { icon: BookMarked, title: "Preserving Stories", text: "We scout and collect historical, ancient, and modern stories from the inhabitants of our towns and villages, safeguarding memory for future generations." },
            { icon: GraduationCap, title: "Scholarships & Grants", text: "We support community development and give scholarships and grants to indigent Yorùbá students, artists, and talented upcoming musicians." },
            { icon: Users2, title: "Community & Identity", text: "We work with local communities to build identity and pride, creating lasting local social capital and cultural participation for all." },
            { icon: Globe, title: "Exchange & Outreach", text: "We are committed to international exchange programmes, community outreach, and national and international excursions." },
            { icon: Sparkles, title: "Talent Discovery", text: "We hunt for talent in all facets of creative expression, honouring the creativity and diversity of Yorùbá heritage." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <div className="h-full border border-parchment rounded-xl p-8 bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream mb-6">
                  <c.icon className="w-5 h-5" />
                </span>
                <h3 className="font-serif text-2xl text-ink mb-3">{c.title}</h3>
                <p className="text-ink-muted leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUR VALUES */}
      <section className="bg-sand border-y border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">What We Stand For</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mb-14">Our Values</h2>
          </Reveal>
          <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
            {[
              "We affirm the basic right to free expression.",
              "We promote active participation in cultural life.",
              "We honour and respect the creativity and diversity of Yorùbá traditions and people around the world, and in every social media space.",
              "We support cultural equity in all we do, believing that all people everywhere have the right to inherit and develop their intellectual, emotional, material, spiritual, and cultural traditions.",
              "We are serious about delivering results.",
              "We are committed to innovation in our work.",
              "We value the cultural reminiscence held in our collections.",
              "We are committed to our donors, volunteers, and all who count on us.",
              "We create a positive work environment.",
              "We are open-minded.",
            ].map((value, i) => (
              <Reveal key={i} delay={(i % 2) * 0.05}>
                <div className="flex gap-4 items-start border-b border-parchment pb-6" data-testid={`value-${i}`}>
                  <span className="font-serif text-2xl text-gold shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-lg text-ink-muted leading-relaxed">{value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LANGUAGE TEACHING */}
      <section className="bg-forest text-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <img src={IMAGES.geleMulti} alt="Yorùbá language learning" className="rounded-xl w-full object-cover aspect-[4/3]" />
              <div className="absolute -bottom-6 -right-6 hidden md:block w-40 h-40 border border-gold/40 rounded-xl -z-0" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-4">Learn the Language</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              Yorùbá for Every Age & Level
            </h2>
            <p className="text-cream/75 text-lg leading-relaxed mb-8">
              We teach Yorùbá to every age group — from absolute beginners to bilingual speakers —
              with a large variety of subjects and materials arranged for group classes and private
              tuition.
            </p>
            <ul className="space-y-3">
              {[
                "General Yorùbá courses",
                "Conversation classes",
                "Cultural workshops",
                "Group classes & private tuition",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-cream/85">
                  <Check className="w-5 h-5 text-gold shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-5">Begin the Journey</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink mb-8">
            Preserving Heritage. Sharing Knowledge. Inspiring Identity.
          </h2>
          <Link
            to="/topic/origins-and-history"
            className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-8 py-4 rounded-full hover:bg-forest-light transition-colors"
            data-testid="about-start-learning-button"
          >
            Start Learning <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      <Newsletter />
    </div>
  );
}
